import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { ArrowClockwise, At, DownloadSimple, Envelope, Printer } from 'phosphor-react';
import { EmailDelivery } from '@dill/dill-shared';
import { DillButton, DillColumn, DillTable } from '../../../general/Dill';
import { useAppDispatch } from '../../../../redux/store';
import {
  getSupplierDocumentDeliveries,
  getSupplierEmailDeliveries,
} from '../../../../redux/services/supplierBuyersService';
import { DeliveryTimelinePanel, TimelineStep } from './DeliveryTimelinePanel';
import {
  EMAIL_FAILED_STATUSES,
  EMAIL_STATUS_STYLES,
  buildEmailSteps,
  toEmailRow,
} from './SupplierEmailDeliveries';
import { DeliveryRow, buildMailFaxSteps, getDestinationLabel } from './SupplierMailFaxDeliveries';

// One delivery that didn't reach the customer: an email that bounced / was blocked, dropped or
// failed to send, or a letter or fax that failed. The supplier asks the customer for new details
// so the next send goes to the right place.
type ExceptionRow = {
  id: string;
  failedAt: Date | null;
  customerName: string;
  documentName: string;
  documentTypeLabel: string;
  channelLabel: 'Email' | 'Mail' | 'Fax';
  destination: string;
  statusLabel: string;
  reason: string;
  steps: TimelineStep[];
};

const toDate = (value?: Date | string | null) => (value ? new Date(value) : null);

const emailExceptionRow = (delivery: EmailDelivery): ExceptionRow => {
  const row = toEmailRow(delivery);
  return {
    id: `email_${delivery.id}`,
    failedAt: row.failedAt ?? row.sentAt,
    customerName: row.customerName,
    documentName: row.documentName,
    documentTypeLabel: row.documentTypeLabel,
    channelLabel: 'Email',
    destination: row.recipientEmail,
    statusLabel: EMAIL_STATUS_STYLES[row.status].label,
    reason: row.statusDetail || '',
    steps: buildEmailSteps(row),
  };
};

const mailFaxExceptionRow = (delivery: DeliveryRow): ExceptionRow => {
  const failedEvent = [...(delivery.statusHistory ?? [])]
    .reverse()
    .find((event) => event.status === 'FAILED');
  return {
    id: `doc_${delivery.id}`,
    failedAt: toDate(failedEvent?.at) ?? toDate(delivery.updatedAt) ?? toDate(delivery.createdAt),
    customerName: delivery.buyerName || '',
    documentName:
      delivery.documentLabel ||
      `${delivery.documentType === 'INVOICE' ? 'Invoice' : 'Statement'} ${delivery.documentId}`,
    documentTypeLabel: delivery.documentType === 'INVOICE' ? 'Invoice' : 'Statement',
    channelLabel: delivery.channel === 'FAX' ? 'Fax' : 'Mail',
    destination: getDestinationLabel(delivery),
    statusLabel: 'Failed',
    reason: delivery.lastError || delivery.statusDetail || '',
    steps: buildMailFaxSteps(delivery),
  };
};

const CHANNEL_ICONS: Record<ExceptionRow['channelLabel'], React.ReactNode> = {
  Email: <At size={14} />,
  Mail: <Envelope size={14} />,
  Fax: <Printer size={14} />,
};

const csvEscape = (value: string) =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const dateInputClassName =
  'h-[31px] rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 outline-none focus:border-blue-400';

function SupplierDeliveryExceptions({ supplierId }: { supplierId: string }) {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<ExceptionRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fromDate, setFromDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [toDateValue, setToDateValue] = useState(moment().format('YYYY-MM-DD'));
  const [selectedRow, setSelectedRow] = useState<ExceptionRow | null>(null);

  const fetchExceptions = useCallback(async () => {
    if (!supplierId) return;
    setIsLoading(true);
    try {
      const [emailResult, documentResult] = await Promise.all([
        dispatch(getSupplierEmailDeliveries({ supplierId })),
        dispatch(getSupplierDocumentDeliveries({ supplierId })),
      ]);
      const emails = (emailResult.payload as { data?: EmailDelivery[] } | null)?.data ?? [];
      const documents = (documentResult.payload as { data?: DeliveryRow[] } | null)?.data ?? [];
      setRows([
        ...emails
          .filter((delivery) => EMAIL_FAILED_STATUSES.includes(delivery.status))
          .map(emailExceptionRow),
        ...documents.filter((delivery) => delivery.status === 'FAILED').map(mailFaxExceptionRow),
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [supplierId, dispatch]);

  useEffect(() => {
    fetchExceptions();
  }, [fetchExceptions]);

  const filteredRows = useMemo(() => {
    const from = fromDate ? moment(fromDate, 'YYYY-MM-DD').startOf('day') : null;
    const to = toDateValue ? moment(toDateValue, 'YYYY-MM-DD').endOf('day') : null;
    return rows.filter((row) => {
      if (!row.failedAt) return !from && !to;
      const failedAt = moment(row.failedAt);
      return (!from || failedAt.isSameOrAfter(from)) && (!to || failedAt.isSameOrBefore(to));
    });
  }, [rows, fromDate, toDateValue]);

  const handleExport = () => {
    const header = [
      'Date',
      'Customer',
      'Type',
      'Document',
      'Channel',
      'Not delivered to',
      'Status',
      'Reason',
    ];
    const lines = [...filteredRows]
      .sort((a, b) => (a.failedAt?.getTime() ?? 0) - (b.failedAt?.getTime() ?? 0))
      .map((row) => [
        row.failedAt ? moment(row.failedAt).format('YYYY-MM-DD h:mm A') : '',
        row.customerName,
        row.documentTypeLabel,
        row.documentName,
        row.channelLabel,
        row.destination,
        row.statusLabel,
        row.reason,
      ]);
    const csv = [header, ...lines].map((line) => line.map(csvEscape).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `delivery-exceptions-${fromDate || 'start'}-to-${toDateValue || 'today'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo<DillColumn<ExceptionRow>[]>(
    () => [
      {
        key: 'failedAt',
        label: 'Date',
        width: 130,
        sortable: true,
        getSortValue: (row) => row.failedAt?.getTime() ?? 0,
        render: (_: any, row) => (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900">
              {row.failedAt ? moment(row.failedAt).format('MMM D, YYYY') : '-'}
            </span>
            <span className="text-xs text-gray-500">
              {row.failedAt ? moment(row.failedAt).format('h:mm A') : ''}
            </span>
          </div>
        ),
      },
      {
        key: 'customerName',
        label: 'Customer',
        width: 200,
        sortable: true,
        filterable: true,
        getSearchValue: (row) => row.customerName,
        render: (_: any, row) => (
          <span
            className="text-sm text-gray-900 line-clamp-2 break-words min-w-0"
            title={row.customerName}>
            {row.customerName || '-'}
          </span>
        ),
      },
      {
        key: 'documentName',
        label: 'Document',
        width: 180,
        sortable: true,
        getSearchValue: (row) => row.documentName,
        render: (_: any, row) => (
          <span className="text-sm font-medium text-gray-900 truncate" title={row.documentName}>
            {row.documentName}
          </span>
        ),
      },
      {
        key: 'documentTypeLabel',
        label: 'Type',
        width: 110,
        sortable: true,
        filterable: true,
      },
      {
        key: 'channelLabel',
        label: 'Channel',
        width: 110,
        sortable: true,
        filterable: true,
        render: (_: any, row) => (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
            {CHANNEL_ICONS[row.channelLabel]}
            {row.channelLabel}
          </span>
        ),
      },
      {
        key: 'destination',
        label: 'Not delivered to',
        width: 260,
        sortable: true,
        getSearchValue: (row) => row.destination,
        render: (_: any, row) => (
          <span
            className="text-sm text-gray-900 line-clamp-2 break-words min-w-0"
            title={row.destination}>
            {row.destination || '-'}
          </span>
        ),
      },
      {
        key: 'statusLabel',
        label: 'Status',
        width: 150,
        sortable: true,
        filterable: true,
        render: (_: any, row) => (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
            {row.statusLabel}
          </span>
        ),
      },
      {
        key: 'reason',
        label: 'Reason',
        width: 300,
        getSearchValue: (row) => row.reason,
        render: (_: any, row) => (
          <span
            className="text-xs text-gray-600 line-clamp-2 break-words min-w-0"
            title={row.reason}>
            {row.reason || '-'}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="w-full h-full flex flex-col">
      <div className="mb-3 flex items-center gap-2 text-xs text-gray-600">
        <span>From</span>
        <input
          type="date"
          value={fromDate}
          max={toDateValue || undefined}
          onChange={(e) => setFromDate(e.target.value)}
          className={dateInputClassName}
        />
        <span>to</span>
        <input
          type="date"
          value={toDateValue}
          min={fromDate || undefined}
          onChange={(e) => setToDateValue(e.target.value)}
          className={dateInputClassName}
        />
      </div>
      <div className="flex-1 min-h-0">
        <DillTable
          columns={columns}
          data={filteredRows}
          rowKey={(row) => row.id}
          onRowClick={(row) => setSelectedRow(row)}
          search
          searchPlaceholder="Search customers, documents, emails or addresses"
          sortable
          defaultSort={{ key: 'failedAt', dir: 'desc' }}
          loading={isLoading}
          rowHeight={64}
          striped={false}
          resizable
          showActiveFilters
          countLabel="Exceptions"
          emptyMessage="Every invoice and statement in this date range was delivered."
          toolbarRight={
            <div className="flex items-center gap-2">
              <DillButton
                label="Export CSV"
                variant="secondary"
                icon={<DownloadSimple size={14} />}
                onClick={handleExport}
                isDisabled={isLoading || filteredRows.length === 0}
                height={31}
              />
              <DillButton
                label="Refresh"
                variant="secondary"
                icon={<ArrowClockwise size={14} />}
                onClick={fetchExceptions}
                isDisabled={isLoading}
                height={31}
              />
            </div>
          }
        />
      </div>
      {selectedRow && (
        <DeliveryTimelinePanel
          title={selectedRow.documentName}
          subtitle={`${selectedRow.channelLabel} · ${selectedRow.statusLabel}`}
          facts={[
            { label: 'Customer', value: selectedRow.customerName },
            { label: 'Not delivered to', value: selectedRow.destination },
            { label: 'Reason', value: selectedRow.reason },
          ]}
          steps={selectedRow.steps}
          onClose={() => setSelectedRow(null)}
        />
      )}
    </div>
  );
}

export default SupplierDeliveryExceptions;
